/** Cap on Field Notes a person writes; the API and Oak's editor must enforce the same number. */
export const MAX_FIELD_NOTES_LENGTH = 4_000;

/** One bulk gather's ceiling: each violation is a paid Scout turn. Shared so Oak and the API refuse at the same size. */
export const MAX_BULK_FIELD_NOTES_GATHER = 100;

/** The notes without bold markers or link targets, for exports and other plain-text surfaces. */
export function fieldNotesPlainText(content: string): string {
  return content
    .replace(/\[([^\]]+)\]\((?:[^()\s]|\([^()\s]*\))*(?:\s+"[^"]*")?\)/g, "$1")
    .replace(/\*\*(.+?)\*\*/g, "$1");
}

/** A citation link as Oak renders one: any title starting with "cite", in any case. Stored as `[label](url "cite")`. */
const CITATION_PATTERN = /\[([^\]]+)\]\(((?:[^()\s]|\([^()\s]*\))*)\s+"cite[^"]*"\)/gi;

export type FieldNotesCitation = { label: string; url: string };

function citationLink({ label, url }: FieldNotesCitation): string {
  return `[${label}](${url} "cite")`;
}

/** The notes' citations, in order. */
export function fieldNotesCitations(content: string): FieldNotesCitation[] {
  return [...content.matchAll(CITATION_PATTERN)].map(([, label = "", url = ""]) => ({
    label,
    url,
  }));
}

/** The notes with each citation shown as its label, for editing. */
export function citationsAsLabels(content: string): string {
  return content.replace(CITATION_PATTERN, "$1");
}

/** Where `label` appears as whole text: not inside a longer word, number, or section. */
function wholeLabelAt(text: string, label: string, at: number): boolean {
  const before = text[at - 1] ?? "";
  const after = text.slice(at + label.length, at + label.length + 2);
  return !/\w/.test(before) && !/^(?:[\w(]|\.\w)/.test(after);
}

/** Edited text with its citations back on the labels a person kept, in any order. */
export function withCitations(text: string, citations: FieldNotesCitation[]): string {
  const plain = citationsAsLabels(text);
  const placed: { at: number; citation: FieldNotesCitation }[] = [];
  const taken = (at: number, length: number) =>
    placed.some((p) => at < p.at + p.citation.label.length && p.at < at + length);
  for (const citation of citations) {
    let at = plain.indexOf(citation.label);
    while (
      at !== -1 &&
      (taken(at, citation.label.length) || !wholeLabelAt(plain, citation.label, at))
    ) {
      at = plain.indexOf(citation.label, at + 1);
    }
    if (at !== -1) placed.push({ at, citation });
  }
  return placed
    .sort((a, b) => b.at - a.at)
    .reduce(
      (result, { at, citation }) =>
        result.slice(0, at) + citationLink(citation) + result.slice(at + citation.label.length),
      plain
    );
}

/** A person's save keeps only citations the notes already had, so no one can add one by hand. */
export function keepKnownCitations(content: string, previous: string | null): string {
  const known = new Set(fieldNotesCitations(previous ?? "").map(citationLink));
  return content.replace(CITATION_PATTERN, (_link, label: string, url: string) => {
    const link = citationLink({ label, url });
    return known.has(link) ? link : label;
  });
}
